import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiFilePlus, FiSave, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import {
  apiErrorMessage,
  Feed,
  FeedAnalysisAlerts,
  feedAnalysisService,
  FeedFormula,
  NutrientKey,
  NUTRIENTS,
  PreviewResult,
  ReferencedNutrientKey,
} from '../../../../services/feedAnalysisService';
import { AlertsList } from '../components/AlertsList';
import { MonthSelector } from '../components/MonthSelector';
import {
  currentMonth,
  formatNumber,
  monthInput,
  monthLong,
  STATUS_STYLE,
  WARNING_INFO,
} from '../format';
import { PageShell } from '../PageShell';
import { readLabReport } from '../pdf/readLabReport';

type TextFields = {
  sample_code: string;
  item_name: string;
  feed_id: string;
  lot: string;
  manufactured_on: string;
  collected_on: string;
  received_on: string;
  released_at: string;
  laboratory: string;
};

interface ImportRow {
  uid: string;
  file: File | null;
  fileName: string;
  reading: boolean;
  readError: string;
  unknownAnalyses: string[];
  fields: TextFields;
  values: Record<NutrientKey, string>;
  preview: PreviewResult | null;
  include: boolean;
  saving: boolean;
  savedId: string | null;
  saveError: string;
}

const EMPTY_VALUES = Object.fromEntries(
  NUTRIENTS.map(item => [item.key, '']),
) as Record<NutrientKey, string>;

const EMPTY_FIELDS: TextFields = {
  sample_code: '',
  item_name: '',
  feed_id: '',
  lot: '',
  manufactured_on: '',
  collected_on: '',
  received_on: '',
  released_at: '',
  laboratory: '',
};

let uidCounter = 0;
const nextUid = () => {
  uidCounter += 1;
  return `row-${uidCounter}`;
};

// "17,58" ou "17.58" -> 17.58; vazio -> null
function toNumber(value: string): number | null {
  const text = value.trim().replace(/\s/g, '');
  if (!text) return null;
  const number = Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text,
  );
  return Number.isFinite(number) ? number : null;
}

const numberText = (value: number | undefined) =>
  value === undefined ? '' : String(value).replace('.', ',');

const nullable = (value: string) => value.trim() || null;

function rowValues(row: ImportRow): Record<NutrientKey, number | null> {
  return Object.fromEntries(
    NUTRIENTS.map(item => [item.key, toNumber(row.values[item.key])]),
  ) as Record<NutrientKey, number | null>;
}

function blockingWarnings(row: ImportRow) {
  return (row.preview?.warnings || []).filter(
    warning => WARNING_INFO[warning].blocking,
  );
}

const FeedAnalysisImport: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [month, setMonth] = useState<string>('');
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FeedAnalysisAlerts | null>(null);
  const previewTimer = useRef<number>();

  const loadRegistry = useCallback(async () => {
    const [feedList, formulaList] = await Promise.all([
      feedAnalysisService.listFeeds(),
      feedAnalysisService.listFormulas(),
    ]);
    setFeeds(feedList.filter(item => item.active));
    setFormulas(formulaList.filter(item => item.active));
  }, []);

  useEffect(() => {
    loadRegistry().catch(() =>
      toast({
        title: 'Não foi possível carregar o cadastro de rações.',
        status: 'error',
      }),
    );
  }, [loadRegistry, toast]);

  const updateRow = useCallback(
    (uid: string, change: (row: ImportRow) => Partial<ImportRow>) => {
      setRows(current =>
        current.map(row =>
          row.uid === uid ? { ...row, ...change(row) } : row,
        ),
      );
    },
    [],
  );

  const onDrop = useCallback(
    (files: File[]) => {
      setResult(null);
      const added = files.map(file => ({
        uid: nextUid(),
        file,
        fileName: file.name,
        reading: true,
        readError: '',
        unknownAnalyses: [],
        fields: { ...EMPTY_FIELDS },
        values: { ...EMPTY_VALUES },
        preview: null,
        include: true,
        saving: false,
        savedId: null,
        saveError: '',
      }));
      setRows(current => [...current, ...added]);

      added.forEach(row => {
        readLabReport(row.file)
          .then(report => {
            updateRow(row.uid, () => ({
              reading: false,
              readError: report.missing.includes('results')
                ? 'Não encontrei resultados neste PDF. Confira se é um relatório de ensaio.'
                : '',
              unknownAnalyses: report.results
                .filter(item => !item.key)
                .map(item => item.name),
              fields: {
                ...EMPTY_FIELDS,
                sample_code: report.sample_code || '',
                item_name: report.item_name || '',
                lot: report.lot || '',
                manufactured_on: report.manufactured_on || '',
                collected_on: report.collected_on || '',
                received_on: report.received_on || '',
                released_at: report.released_at || '',
                laboratory: report.laboratory || '',
              },
              values: {
                ...EMPTY_VALUES,
                ...Object.fromEntries(
                  Object.entries(report.values).map(([key, value]) => [
                    key,
                    numberText(value),
                  ]),
                ),
              },
            }));
            // mês das análises: o da coleta do primeiro laudo lido
            setMonth(
              current =>
                current ||
                (report.collected_on || report.manufactured_on || '').slice(
                  0,
                  7,
                ),
            );
          })
          .catch(() =>
            updateRow(row.uid, () => ({
              reading: false,
              readError: 'Não foi possível ler este PDF.',
            })),
          );
      });
    },
    [updateRow],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'application/pdf',
    multiple: true,
  });

  const addManualRow = () => {
    setResult(null);
    setRows(current => [
      ...current,
      {
        uid: nextUid(),
        file: null,
        fileName: 'Lançamento manual',
        reading: false,
        readError: '',
        unknownAnalyses: [],
        fields: { ...EMPTY_FIELDS },
        values: { ...EMPTY_VALUES },
        preview: null,
        include: true,
        saving: false,
        savedId: null,
        saveError: '',
      },
    ]);
    setMonth(current => current || currentMonth());
  };

  // a cada mudança a api casa a ração, resolve a referência e avalia
  const previewKey = useMemo(
    () =>
      JSON.stringify(
        rows
          .filter(row => !row.reading && !row.savedId)
          .map(row => [row.uid, row.fields, row.values]),
      ) + month,
    [rows, month],
  );

  useEffect(() => {
    const pending = rows.filter(row => !row.reading && !row.savedId);
    if (!pending.length || !month) return undefined;

    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      feedAnalysisService
        .preview(
          pending.map(row => ({
            sample_code: nullable(row.fields.sample_code),
            item_name: nullable(row.fields.item_name),
            feed_id: nullable(row.fields.feed_id),
            analysis_month: month,
            ...rowValues(row),
          })),
        )
        .then(previews => {
          const byUid = new Map(
            pending.map((row, index) => [row.uid, previews[index]]),
          );
          setRows(current =>
            current.map(row => {
              const preview = byUid.get(row.uid);
              if (!preview) return row;
              return {
                ...row,
                preview,
                // ração casada pelo nome do laudo já fica selecionada
                fields:
                  !row.fields.feed_id && preview.feed_id
                    ? { ...row.fields, feed_id: preview.feed_id }
                    : row.fields,
                include: row.include && !preview.duplicate_of,
              };
            }),
          );
        })
        .catch(error =>
          toast({
            title: apiErrorMessage(
              error,
              'Não foi possível avaliar os laudos.',
            ),
            status: 'error',
          }),
        );
    }, 400);

    return () => window.clearTimeout(previewTimer.current);
    // previewKey resume rows + month (sem o resultado do preview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  const createFeed = async (row: ImportRow, formulaId: string) => {
    try {
      const feed = await feedAnalysisService.saveFeed({
        name: row.fields.item_name,
        formula_id: formulaId || null,
        active: true,
        notes: null,
      });
      await loadRegistry();
      updateRow(row.uid, current => ({
        fields: { ...current.fields, feed_id: feed.id },
      }));
      toast({ title: `Ração ${feed.name} cadastrada.`, status: 'success' });
    } catch (error) {
      toast({
        title: apiErrorMessage(error, 'Não foi possível cadastrar a ração.'),
        status: 'error',
      });
    }
  };

  const ready = rows.filter(
    row =>
      row.include &&
      !row.savedId &&
      !row.reading &&
      row.preview &&
      !blockingWarnings(row).length,
  );

  const save = async () => {
    setSaving(true);
    let savedCount = 0;

    // uma por vez: o erro de uma (ex.: duplicada) não derruba as outras
    // eslint-disable-next-line no-restricted-syntax
    for (const row of ready) {
      updateRow(row.uid, () => ({ saving: true, saveError: '' }));
      try {
        // eslint-disable-next-line no-await-in-loop
        const sample = await feedAnalysisService.createSample({
          sample_code: row.fields.sample_code.trim(),
          feed_id: row.fields.feed_id,
          item_name: nullable(row.fields.item_name),
          lot: nullable(row.fields.lot),
          manufactured_on: nullable(row.fields.manufactured_on),
          collected_on: nullable(row.fields.collected_on),
          received_on: nullable(row.fields.received_on),
          released_at: nullable(row.fields.released_at),
          laboratory: nullable(row.fields.laboratory),
          analysis_month: month,
          ...rowValues(row),
        });
        let saveError = '';
        if (row.file) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await feedAnalysisService.uploadReport(sample.id, row.file);
          } catch (error) {
            saveError = `Amostra gravada, mas o PDF não foi anexado: ${apiErrorMessage(
              error,
              'erro no envio',
            )}`;
          }
        }
        savedCount += 1;
        updateRow(row.uid, () => ({
          saving: false,
          savedId: sample.id,
          saveError,
        }));
      } catch (error) {
        updateRow(row.uid, () => ({
          saving: false,
          saveError: apiErrorMessage(error, 'Não foi possível gravar.'),
        }));
      }
    }

    setSaving(false);

    if (savedCount) {
      toast({
        title: `${savedCount} amostra(s) importada(s).`,
        status: 'success',
      });
      try {
        setResult(await feedAnalysisService.alerts(month));
      } catch {
        setResult(null);
      }
    }
  };

  const feedOptions = useMemo(
    () => [...feeds].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [feeds],
  );

  return (
    <PageShell
      title="Importar laudos"
      subtitle="Solte os PDFs do relatório de ensaio: o sistema preenche os campos, você confere e grava."
      actions={
        month ? (
          <MonthSelector
            value={month}
            onChange={value => {
              setResult(null);
              setMonth(value);
            }}
          />
        ) : undefined
      }
    >
      <Box
        {...getRootProps()}
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.600'}
        bg={isDragActive ? 'gray.700' : 'gray.800'}
        borderRadius="lg"
        p={{ base: '6', md: '10' }}
        textAlign="center"
        cursor="pointer"
        mb="4"
      >
        <input {...getInputProps()} />
        <Flex direction="column" align="center" gridGap="2">
          <FiUploadCloud size={36} />
          <Text fontWeight="600">
            Arraste os PDFs dos laudos aqui ou clique para escolher
          </Text>
          <Text fontSize="sm" color="gray.400">
            Um PDF por amostra. A leitura acontece no seu navegador.
          </Text>
        </Flex>
      </Box>

      <Flex
        justify="space-between"
        align="center"
        mb="6"
        gridGap="3"
        flexWrap="wrap"
      >
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FiFilePlus />}
          onClick={addManualRow}
        >
          Lançar sem PDF
        </Button>
        {rows.length > 0 && (
          <Flex gridGap="3" align="center">
            <Text fontSize="sm" color="gray.400">
              {ready.length} de {rows.filter(row => !row.savedId).length}{' '}
              pronta(s) para gravar
              {month ? ` em ${monthLong(`${month}-01`)}` : ''}
            </Text>
            <Button
              colorScheme="blue"
              leftIcon={<FiSave />}
              onClick={save}
              isLoading={saving}
              isDisabled={!ready.length}
            >
              Gravar amostras
            </Button>
          </Flex>
        )}
      </Flex>

      {result && (
        <Box mb="6">
          <Flex
            justify="space-between"
            align="center"
            mb="3"
            gridGap="2"
            flexWrap="wrap"
          >
            <Heading size="md" fontWeight="600">
              Alertas de {monthLong(result.month)}
            </Heading>
            <Button
              as={Link}
              to={`/feed-analysis?month=${monthInput(result.month)}`}
              size="sm"
              colorScheme="blue"
              variant="outline"
            >
              Ver painel do mês
            </Button>
          </Flex>
          <AlertsList
            alerts={result.alerts}
            emptyText="Nenhum alerta: todos os nutrientes dentro da faixa."
          />
        </Box>
      )}

      <Stack spacing="4">
        {rows.map(row => (
          <ImportRowCard
            key={row.uid}
            row={row}
            feeds={feedOptions}
            formulas={formulas}
            onChange={change => updateRow(row.uid, change)}
            onRemove={() => {
              setRows(current => current.filter(item => item.uid !== row.uid));
            }}
            onCreateFeed={formulaId => createFeed(row, formulaId)}
          />
        ))}
      </Stack>
    </PageShell>
  );
};

interface ImportRowCardProps {
  row: ImportRow;
  feeds: Feed[];
  formulas: FeedFormula[];
  onChange: (change: (row: ImportRow) => Partial<ImportRow>) => void;
  onRemove: () => void;
  onCreateFeed: (formulaId: string) => void;
}

const ImportRowCard: React.FC<ImportRowCardProps> = ({
  row,
  feeds,
  formulas,
  onChange,
  onRemove,
  onCreateFeed,
}) => {
  const [newFeedFormula, setNewFeedFormula] = useState('');
  const locked = !!row.savedId || row.saving;
  const warnings = row.preview?.warnings || [];
  const blocking = blockingWarnings(row);
  let borderColor = 'gray.700';
  if (row.savedId) borderColor = 'green.500';
  else if (blocking.length) borderColor = 'yellow.500';
  const setField = (field: keyof TextFields, value: string) =>
    onChange(current => ({ fields: { ...current.fields, [field]: value } }));

  return (
    <Box
      bg="gray.800"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p="4"
      opacity={row.include || row.savedId ? 1 : 0.6}
    >
      <Flex
        justify="space-between"
        align="center"
        mb="3"
        gridGap="2"
        flexWrap="wrap"
      >
        <Flex align="center" gridGap="3" minW="0">
          {!row.savedId && (
            <Checkbox
              isChecked={row.include}
              onChange={event => {
                onChange(() => ({ include: event.target.checked }));
              }}
              isDisabled={locked}
            >
              Gravar
            </Checkbox>
          )}
          <Text fontWeight="600" isTruncated title={row.fileName}>
            {row.fileName}
          </Text>
          {row.reading && <Spinner size="sm" />}
          {row.savedId && (
            <Badge colorScheme="green">
              <Link to={`/feed-analysis/samples/${row.savedId}`}>Gravada</Link>
            </Badge>
          )}
        </Flex>
        {!row.savedId && (
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<FiTrash2 />}
            onClick={onRemove}
            isDisabled={locked}
          >
            Remover
          </Button>
        )}
      </Flex>

      {row.readError && (
        <Text color="yellow.300" fontSize="sm" mb="3">
          {row.readError}
        </Text>
      )}
      {row.saveError && (
        <Text
          color={row.savedId ? 'yellow.300' : 'red.300'}
          fontSize="sm"
          mb="3"
        >
          {row.saveError}
        </Text>
      )}

      {warnings.length > 0 && !row.savedId && (
        <Flex gridGap="2" flexWrap="wrap" mb="3">
          {warnings.map(warning => (
            <Badge
              key={warning}
              colorScheme={WARNING_INFO[warning].blocking ? 'yellow' : 'gray'}
            >
              {WARNING_INFO[warning].label}
              {warning === 'duplicate' && row.preview?.duplicate_of && (
                <> ({row.preview.duplicate_of.feed_name})</>
              )}
            </Badge>
          ))}
        </Flex>
      )}

      {row.unknownAnalyses.length > 0 && (
        <Text fontSize="xs" color="gray.400" mb="3">
          Análises do laudo não usadas no controle:{' '}
          {row.unknownAnalyses.join(', ')}
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="3" mb="4">
        <FormControl isRequired>
          <FormLabel fontSize="xs">Código da amostra</FormLabel>
          <Input
            size="sm"
            value={row.fields.sample_code}
            onChange={event => setField('sample_code', event.target.value)}
            isDisabled={locked}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="xs">
            Ração{' '}
            {row.fields.item_name && (
              <Text as="span" color="gray.400" fontWeight="400">
                (laudo: {row.fields.item_name})
              </Text>
            )}
          </FormLabel>
          <Select
            size="sm"
            placeholder="Selecione"
            value={row.fields.feed_id}
            onChange={event => setField('feed_id', event.target.value)}
            isDisabled={locked}
          >
            {feeds.map(feed => (
              <option key={feed.id} value={feed.id}>
                {feed.name}
                {feed.formula_code
                  ? ` — ${feed.formula_code}`
                  : ' — sem fórmula'}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Lote</FormLabel>
          <Input
            size="sm"
            value={row.fields.lot}
            onChange={event => setField('lot', event.target.value)}
            isDisabled={locked}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Data de coleta</FormLabel>
          <Input
            size="sm"
            type="date"
            value={row.fields.collected_on}
            onChange={event => setField('collected_on', event.target.value)}
            isDisabled={locked}
          />
        </FormControl>
      </SimpleGrid>

      {warnings.includes('feed_not_found') &&
        row.fields.item_name &&
        !row.fields.feed_id &&
        !locked && (
          <Flex
            bg="gray.700"
            borderRadius="md"
            p="3"
            mb="4"
            gridGap="3"
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
          >
            <Text fontSize="sm" flex="1">
              “{row.fields.item_name}” não está no cadastro de rações.
            </Text>
            <Select
              size="sm"
              w={{ base: '100%', md: '240px' }}
              placeholder="Fórmula do nutricionista"
              value={newFeedFormula}
              onChange={event => setNewFeedFormula(event.target.value)}
            >
              {formulas.map(formula => (
                <option key={formula.id} value={formula.id}>
                  {formula.code}
                  {formula.description ? ` — ${formula.description}` : ''}
                </option>
              ))}
            </Select>
            <Button size="sm" onClick={() => onCreateFeed(newFeedFormula)}>
              Cadastrar ração
            </Button>
          </Flex>
        )}

      <SimpleGrid columns={{ base: 2, sm: 3, md: 5, xl: 9 }} spacing="3">
        {NUTRIENTS.map(nutrient => {
          const evaluation = nutrient.referenced
            ? row.preview?.evaluation[nutrient.key as ReferencedNutrientKey]
            : undefined;
          const style = evaluation?.status
            ? STATUS_STYLE[evaluation.status]
            : null;
          return (
            <FormControl key={nutrient.key}>
              <FormLabel
                fontSize="xs"
                mb="1"
                isTruncated
                title={nutrient.label}
              >
                {nutrient.label}
                {nutrient.unit ? ` (${nutrient.unit})` : ''}
              </FormLabel>
              <Input
                size="sm"
                inputMode="decimal"
                value={row.values[nutrient.key]}
                onChange={event => {
                  const { value } = event.target;
                  onChange(current => ({
                    values: { ...current.values, [nutrient.key]: value },
                  }));
                }}
                borderColor={style?.bg}
                borderWidth={style ? '2px' : '1px'}
                isDisabled={locked}
              />
              {evaluation && evaluation.status && (
                <Text fontSize="xs" mt="1" color="gray.400" whiteSpace="nowrap">
                  <Text as="span" color={style?.bg} fontWeight="700">
                    {style?.symbol} {style?.label}
                  </Text>
                  {evaluation.expected !== null &&
                    ` · esp. ${formatNumber(evaluation.expected)}`}
                </Text>
              )}
            </FormControl>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default FeedAnalysisImport;
